// HallOfWars — public leaderboard page. Lists silos sorted by Elo desc,
// only those with at least one completed non-rehearsal battle. Public.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const TIER_COLORS = {
  Champion: "#A89360",
  Specialist: "#4A5D4E",
  Contender: "#6B6A68",
  Apprentice: "#9B9A98",
};

export default function HallOfWars() {
  const [silos, setSilos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/battles/hall");
        setSilos(r.data.silos || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F2ED]" data-testid="hall-of-wars">
      <header className="border-b border-[#D8D6D2] bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/wars" className="flex items-center gap-2 text-[#1C1B1A] hover:opacity-70" data-testid="hall-back-btn">
            <ArrowLeft size={18} /><span className="text-sm">Battles</span>
          </Link>
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-[#A89360]" />
            <h1 className="text-xl font-semibold tracking-tight text-[#1C1B1A]">Hall of Wars</h1>
          </div>
          <div className="w-[80px]" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-sm text-[#6B6A68] mb-6 leading-relaxed">
          Silos ranked by Elo. Only silos with at least one finished match show up here. Same-owner rehearsals don't count.
        </p>
        {loading ? (
          <div className="text-center py-20 text-[#6B6A68] text-sm">Loading…</div>
        ) : silos.length === 0 ? (
          <div className="bg-white border border-[#D8D6D2] rounded-lg p-12 text-center">
            <Trophy size={32} className="mx-auto text-[#A89360] mb-3" />
            <p className="text-[#1C1B1A]">No completed battles yet. Be the first.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#D8D6D2] rounded-lg overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_auto_auto] gap-3 px-4 py-3 border-b border-[#EFEDE8] bg-[#FAFAFA] text-[10px] uppercase tracking-widest text-[#6B6A68]">
              <span>#</span><span>Silo</span><span>Tier</span><span>Elo</span>
            </div>
            {silos.map((s, i) => (
              <div
                key={s.id}
                className="grid grid-cols-[40px_1fr_auto_auto] gap-3 px-4 py-3 border-b border-[#EFEDE8] last:border-0 items-center"
                data-testid={`hall-row-${s.id}`}
              >
                <span className={`text-base font-semibold ${i === 0 ? "text-[#A89360]" : "text-[#6B6A68]"}`}>
                  {i + 1}{i === 0 && " 🔥"}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1C1B1A] truncate">{s.site_name}</div>
                  <div className="text-[10px] text-[#6B6A68]">
                    {s.owner_name && <>by {s.owner_name} · </>}
                    {s.record?.wins || 0}W – {s.record?.losses || 0}L
                    {(s.record?.draws || 0) > 0 ? ` – ${s.record.draws}D` : ""}
                  </div>
                </div>
                <span
                  className="text-[10px] uppercase tracking-widest font-semibold"
                  style={{ color: TIER_COLORS[s.tier] || "#6B6A68" }}
                >
                  {s.tier}
                </span>
                <span className="text-base font-semibold text-[#1C1B1A] tabular-nums">{s.elo}</span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
